<template>
  <div id="app" class="flex flex-col h-screen overflow-hidden">
    <NavigationProgressBar />
    <!-- Sticky Header (hidden for public routes) -->
    <header v-if="authStore.isAuthenticated && !isPublicRoute" class="app-header">
      <nav class="app-header__nav">
        <div class="app-header__inner">
          <div class="app-header__left">
            <router-link to="/" class="app-header__logo group">
              <img src="/sc-icon-header.svg" alt="Site Compass Logo" class="app-header__logo-img" />
              <span class="app-header__logo-text"> Site Compass Portal </span>
            </router-link>
            <div class="app-header__links">
              <router-link
                to="/"
                class="app-header__link"
                active-class="app-header__link--active"
                exact-active-class="app-header__link--active"
              >
                Dashboard
              </router-link>
              <router-link
                v-if="
                  authStore.hasRole('admin') ||
                  authStore.hasRole('survey-builder') ||
                  authStore.hasRole('survey-admin')
                "
                to="/builder"
                class="app-header__link"
                active-class="app-header__link--active"
              >
                Survey Builder
              </router-link>
              <router-link
                to="/sites"
                class="app-header__link"
                active-class="app-header__link--active"
              >
                Sites & Surveys
              </router-link>
              <router-link
                to="/adhoc-reports"
                class="app-header__link"
                active-class="app-header__link--active"
              >
                Adhoc Reports
              </router-link>
            </div>
          </div>
          <div class="app-header__right">
            <OrgSwitcher />
            <NotificationBell />
            <UserMenu />
          </div>
        </div>
      </nav>
    </header>
    <!-- Main content with padding for footer -->
    <main class="flex-1 overflow-y-auto bg-charcoal-950" :class="{ 'pb-20': !isPublicRoute }">
      <router-view />
    </main>
    <!-- Sticky Footer (hidden for public routes) -->
    <AppFooter v-if="!isPublicRoute" />
    <EnvironmentIndicator v-if="!isPublicRoute" />
    <!-- Developer-only error console. #982: re-mounted here (a flex-shrink-0 child of #app,
         so it docks at the bottom and main content stops above it) — the mount was dropped
         by accident in #406, leaving the "Show Dev Console" profile toggle a no-op. The panel
         reads prefs.devConsoleVisible itself and renders nothing when the toggle is off. -->
    <DevConsolePanel />
    <SessionExpiredDialog ref="sessionExpiredDialog" />
    <!-- Global toast host — single, app-wide, driven by the useToast service.
         Teleported to body + z-[60] so toasts clear open dialogs and survive a
         dialog unmounting after an action. -->
    <Teleport to="body">
      <Toast
        :show="toastState.show"
        :message="toastState.message"
        :variant="toastState.variant"
        :duration="0"
        @close="toast.dismiss"
      />
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth.store'
import UserMenu from '@/components/UserMenu.vue'
import OrgSwitcher from '@/components/OrgSwitcher.vue'
import NotificationBell from '@/components/NotificationBell.vue'
import AppFooter from '@/components/AppFooter.vue'
import EnvironmentIndicator from '@/components/EnvironmentIndicator.vue'
import SessionExpiredDialog from '@/components/SessionExpiredDialog.vue'
import DevConsolePanel from '@/components/DevConsolePanel.vue'
import { useTokenRefresh } from '@/composables/useTokenRefresh'
import { useInactivityTimeout } from '@/composables/useInactivityTimeout'
import { onSessionExpired } from '@/utils/auth-error-handler'
import { useStorageInitialization } from '@/composables/useStorageInitialization'
import NavigationProgressBar from '@/components/ui/NavigationProgressBar.vue'
import Toast from '@/components/ui/Toast.vue'
import { useToast, toastState } from '@/composables/useToast'

const toast = useToast()
const route = useRoute()
const authStore = useAuthStore()
const sessionExpiredDialog = ref<InstanceType<typeof SessionExpiredDialog> | null>(null)

// Check if current route is public
const isPublicRoute = computed(() => route.meta.requiresAuth === false)

// Initialize SQL storage with auth when ready
useStorageInitialization()

// Set up token refresh monitoring (keeps the Auth0 token alive)
const { startMonitoring: startTokenMonitoring, stopMonitoring: stopTokenMonitoring } =
  useTokenRefresh(() => {
    // Show session expired dialog when token refresh fails
    sessionExpiredDialog.value?.open()
  })

// Set up inactivity timeout monitoring (signs out after 6 hours of no activity).
// On timeout, perform a proper logout so auth state is cleared and token refresh stops.
const { startMonitoring: startInactivityMonitoring, stopMonitoring: stopInactivityMonitoring } =
  useInactivityTimeout(() => {
    authStore.logout({ returnTo: window.location.origin + '/login' })
  })

// Watch authentication state to start/stop both monitors
watch(
  () => authStore.isAuthenticated,
  isAuthenticated => {
    if (isAuthenticated) {
      startTokenMonitoring()
      startInactivityMonitoring()
    } else {
      stopTokenMonitoring()
      stopInactivityMonitoring()
    }
  },
  { immediate: true }
)

// Listen for global session expired events (e.g., from API 401 errors)
let unsubscribe: (() => void) | null = null

onMounted(() => {
  unsubscribe = onSessionExpired(() => {
    sessionExpiredDialog.value?.open()
  })
})

onUnmounted(() => {
  if (unsubscribe) {
    unsubscribe()
  }
})
</script>

<style scoped>
#app {
  background: transparent;
}
</style>
